import type {
  ClinicStat,
  DoctorStat,
  InvitedUser,
  PendingInvitation,
  StatUser,
} from '@/api/stats/statsQueries';
import {
  getClinicStatsQuery,
  getDoctorStatsQuery,
  getUserStatsQuery,
} from '@/api/stats/statsQueries';
import {
  SlimTable,
  SlimTableBody,
  SlimTableCell,
  SlimTableHead,
  SlimTableHeader,
  SlimTableRow,
} from '@/components/common/SlimTable';
import { cn } from '@/lib/cn';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useCallback, useState } from 'react';
import { DashboardTableAccordion } from '../DashboardTableAccordion';
import type { StatisticsDoctorAppliedFilters } from './StatisticsDoctorFilters';
import { StatisticsDoctorFilters } from './StatisticsDoctorFilters';

const INITIAL_STATISTICS_FILTERS: StatisticsDoctorAppliedFilters = {
  category: 'doctors',
  dateFrom: '',
  dateTo: '',
};

const fmt = (value: number) => `${value.toFixed(2)} €`;

function getClinicDisplay(doctor: DoctorStat): string {
  if (doctor.clinics.length === 0) return 'no clinic';
  if (doctor.clinics.length === 1) return doctor.clinics[0].clinic;
  return doctor.clinics.map((c) => c.clinic).join(', ');
}

function toPendingInvitations(value: unknown): Array<PendingInvitation> {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      const name = record.name;
      const surname = record.surname;
      const email = record.email;
      const uuid = record.uuid;
      if (typeof name !== 'string' || typeof surname !== 'string') return null;
      return {
        uuid: typeof uuid === 'string' ? uuid : '',
        email: typeof email === 'string' ? email : '',
        name,
        surname,
      };
    })
    .filter((item): item is PendingInvitation => item !== null);
}

function revenueFmt(
  u: StatUser | InvitedUser,
  key:
    | 'first_time_order_amount'
    | 'accruing_revenue'
    | 'period_revenue'
    | 'total_revenue',
): string {
  const n = (u as InvitedUser)[key];
  return typeof n === 'number' ? fmt(n) : '—';
}

function ExpandedDetails({
  newUsers,
  pendingInvitations,
  pendingCount,
  colSpan,
}: {
  newUsers: Array<StatUser | InvitedUser>;
  pendingInvitations: Array<PendingInvitation>;
  pendingCount: number;
  colSpan: number;
}) {
  return (
    <tr className="bg-muted [&>td]:bg-muted">
      <td colSpan={colSpan} className="rounded-b-xl pt-1 pr-4 pb-4 pl-10.5">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div className="min-w-0 sm:col-span-2">
            <p className="mb-2 text-sm font-semibold">New Users</p>
            {newUsers.length === 0 ? (
              <p className="text-muted-foreground text-sm">No new users</p>
            ) : (
              <SlimTable>
                <SlimTableHeader>
                  <SlimTableRow>
                    <SlimTableHead className="align-start flex text-sm">
                      Patient name
                    </SlimTableHead>
                    <SlimTableHead className="text-sm">
                      New revenue
                    </SlimTableHead>
                    <SlimTableHead className="text-sm">
                      Accuring revenue
                    </SlimTableHead>
                    <SlimTableHead className="text-sm">
                      Period revenue
                    </SlimTableHead>
                    <SlimTableHead className="text-sm">
                      Total revenue
                    </SlimTableHead>
                  </SlimTableRow>
                </SlimTableHeader>
                <SlimTableBody>
                  {newUsers.map((u) => (
                    <SlimTableRow key={u.id}>
                      <SlimTableCell className="text-sm">
                        {u.name} {u.surname}
                      </SlimTableCell>
                      <SlimTableCell className="text-sm">
                        {revenueFmt(u, 'first_time_order_amount')}
                      </SlimTableCell>
                      <SlimTableCell className="text-sm">
                        {revenueFmt(u, 'accruing_revenue')}
                      </SlimTableCell>
                      <SlimTableCell className="text-sm">
                        {revenueFmt(u, 'period_revenue')}
                      </SlimTableCell>
                      <SlimTableCell className="text-sm">
                        {revenueFmt(u, 'total_revenue')}
                      </SlimTableCell>
                    </SlimTableRow>
                  ))}
                </SlimTableBody>
              </SlimTable>
            )}
          </div>
          <div className="border-border/50 sm:border-border/50 min-w-0 border-t pt-6 sm:col-span-1 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-6">
            <p className="mb-2 text-sm font-semibold">Invitations (pending)</p>
            {pendingInvitations.length > 0 ? (
              <SlimTable>
                <SlimTableHeader>
                  <SlimTableRow>
                    <SlimTableHead className="text-sm">
                      Patient name
                    </SlimTableHead>
                  </SlimTableRow>
                </SlimTableHeader>
                <SlimTableBody>
                  {pendingInvitations.map((p) => (
                    <SlimTableRow key={p.uuid}>
                      <SlimTableCell className="text-sm">
                        {p.name} {p.surname}
                      </SlimTableCell>
                    </SlimTableRow>
                  ))}
                </SlimTableBody>
              </SlimTable>
            ) : (
              <p className="text-muted-foreground text-sm">
                {pendingCount === 0
                  ? 'No pending invitations'
                  : `${pendingCount} pending`}
              </p>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

export const StatisticsDoctors = ({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) => {
  const [filters, setFilters] = useState<StatisticsDoctorAppliedFilters>(
    INITIAL_STATISTICS_FILTERS,
  );
  const [filtersResetKey, setFiltersResetKey] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const handleAccordionOpen = useCallback(() => {
    setFilters(INITIAL_STATISTICS_FILTERS);
    setFiltersResetKey((k) => k + 1);
    setExpandedId(null);
  }, []);

  const toggleExpand = (id: number) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const apiFilters = {
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined,
  };

  const { data: userStats } = useQuery(getUserStatsQuery());

  const { data: doctorStats, isLoading: isDoctorLoading } = useQuery({
    ...getDoctorStatsQuery(apiFilters),
    enabled: filters.category === 'doctors',
  });

  const { data: clinicStats, isLoading: isClinicLoading } = useQuery({
    ...getClinicStatsQuery(apiFilters),
    enabled: filters.category === 'clinics',
  });

  const isDoctors = filters.category === 'doctors';
  const doctorRows = (doctorStats?.data ?? []).map((d) => ({
    ...d,
    id: d.doctor_id,
  }));
  const clinicRows = (clinicStats?.data ?? []).map((c) => ({
    ...c,
    id: c.clinic_id ?? 0,
  }));
  type RowWithId = (DoctorStat | ClinicStat) & { id: number };
  const rows: Array<RowWithId> = isDoctors ? doctorRows : clinicRows;
  const isLoading = isDoctors ? isDoctorLoading : isClinicLoading;
  const totalClinics =
    userStats?.total_clinics || doctorStats?.summary?.total_clinics || 0;

  const doctorColCount = 8;
  const clinicColCount = 7;

  return (
    <DashboardTableAccordion
      value="statistics-doctors"
      title="Statistics doctors"
      accordionOpenValue={accordionOpenValue}
      onOpen={handleAccordionOpen}
      data={rows}
      isLoading={isLoading}
      emptyMessage="No statistics found."
      loadingMessage="Loading statistics..."
      summarySection={
        userStats ? (
          <div className="grid grid-cols-2 gap-y-1 text-base sm:max-w-sm">
            <span className="text-table-foreground font-medium">
              Total patients:
            </span>
            <span className="text-table-foreground font-light">
              {userStats.total_patients}
            </span>
            <span className="text-table-foreground font-medium">
              Total invitations:
            </span>
            <span className="text-table-foreground font-light">
              {userStats.total_invitations}
            </span>
            <span className="text-table-foreground font-medium">
              Total doctors:
            </span>
            <span className="text-table-foreground font-light">
              {userStats.total_doctors}
            </span>
            <span className="text-table-foreground font-medium">
              Total clinics:
            </span>
            <span className="text-table-foreground font-light">
              {totalClinics}
            </span>
          </div>
        ) : null
      }
      filtersComponent={
        <StatisticsDoctorFilters
          key={filtersResetKey}
          onApply={(newFilters) => setFilters(newFilters)}
        />
      }
      customTable={
        <SlimTable>
          <SlimTableHeader>
            <SlimTableRow>
              <SlimTableHead className="w-8" />
              <SlimTableHead>
                {isDoctors ? 'Specialist name' : 'Clinic name'}
              </SlimTableHead>
              {isDoctors && <SlimTableHead>Clinic</SlimTableHead>}
              <SlimTableHead className="text-center">Total users</SlimTableHead>
              <SlimTableHead className="text-center">New revenue</SlimTableHead>
              <SlimTableHead className="text-center">
                Accuring revenue
              </SlimTableHead>
              <SlimTableHead className="text-center">
                Period revenue
              </SlimTableHead>
              <SlimTableHead className="text-center">
                Total revenue
              </SlimTableHead>
            </SlimTableRow>
          </SlimTableHeader>
          <SlimTableBody>
            {rows.map((row) => {
              const isExpanded = expandedId === row.id;

              if ('surname' in row) {
                const doctor = row;
                return (
                  <ExpandableRow
                    key={doctor.doctor_id}
                    isExpanded={isExpanded}
                    onToggle={() => toggleExpand(doctor.doctor_id)}
                  >
                    <SlimTableCell>
                      {doctor.name} {doctor.surname}
                    </SlimTableCell>
                    <SlimTableCell>{getClinicDisplay(doctor)}</SlimTableCell>
                    <SlimTableCell className="text-center">
                      {doctor.invited_users_count}
                    </SlimTableCell>
                    <SlimTableCell className="text-center">
                      {fmt(doctor.invited_users_new_revenue)}
                    </SlimTableCell>
                    <SlimTableCell className="text-center">
                      {fmt(doctor.invited_users_accruing_revenue)}
                    </SlimTableCell>
                    <SlimTableCell className="text-center">
                      {fmt(doctor.invited_users_period_revenue)}
                    </SlimTableCell>
                    <SlimTableCell className="text-center">
                      {fmt(doctor.invited_users_total_revenue)}
                    </SlimTableCell>
                    {isExpanded && (
                      <ExpandedDetails
                        newUsers={doctor.invited_users ?? []}
                        pendingInvitations={
                          doctor.pending_invitations ??
                          toPendingInvitations(
                            (doctor as { pending_invitations?: unknown })
                              .pending_invitations,
                          )
                        }
                        pendingCount={
                          doctor.pending_invitations_count ??
                          doctor.invitations ??
                          0
                        }
                        colSpan={doctorColCount}
                      />
                    )}
                  </ExpandableRow>
                );
              }

              const clinic = row;
              const clinicId = clinic.clinic_id ?? 0;
              return (
                <ExpandableRow
                  key={clinicId}
                  isExpanded={isExpanded}
                  onToggle={() => toggleExpand(clinicId)}
                >
                  <SlimTableCell>{clinic.clinic}</SlimTableCell>
                  <SlimTableCell className="text-center">
                    {clinic.total_users}
                  </SlimTableCell>
                  <SlimTableCell className="text-center">
                    {fmt(clinic.new_revenue)}
                  </SlimTableCell>
                  <SlimTableCell className="text-center">
                    {fmt(clinic.accruing_revenue)}
                  </SlimTableCell>
                  <SlimTableCell className="text-center">
                    {fmt(clinic.period_revenue)}
                  </SlimTableCell>
                  <SlimTableCell className="text-center">
                    {fmt(clinic.total_revenue)}
                  </SlimTableCell>
                  {isExpanded && (
                    <ExpandedDetails
                      newUsers={clinic.users ?? clinic.invited_users ?? []}
                      pendingInvitations={
                        Array.isArray(clinic.pending_invitations)
                          ? clinic.pending_invitations
                          : toPendingInvitations(
                              (clinic as { pending_invitations?: unknown })
                                .pending_invitations,
                            )
                      }
                      pendingCount={
                        clinic.pending_invitations_count ??
                        (Array.isArray(clinic.pending_invitations)
                          ? clinic.pending_invitations.length
                          : 0)
                      }
                      colSpan={clinicColCount}
                    />
                  )}
                </ExpandableRow>
              );
            })}
          </SlimTableBody>
        </SlimTable>
      }
    />
  );
};

function ExpandableRow({
  isExpanded,
  onToggle,
  children,
}: {
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const childArray = Array.isArray(children) ? children : [children];
  const cells = childArray.filter(
    (c) =>
      c && typeof c === 'object' && 'type' in c && c.type !== ExpandedDetails,
  );
  const expandedDetail = childArray.find(
    (c) =>
      c && typeof c === 'object' && 'type' in c && c.type === ExpandedDetails,
  );

  return (
    <>
      <SlimTableRow
        className={cn(
          'cursor-pointer',
          isExpanded &&
            '[&>td]:bg-muted [&>td:first-child]:rounded-tl-xl [&>td:last-child]:rounded-tr-xl',
        )}
        onClick={onToggle}
      >
        <SlimTableCell className="w-8">
          <ChevronDown
            className={cn(
              'text-muted-foreground size-4 transition-transform duration-200',
              isExpanded && 'rotate-180',
            )}
          />
        </SlimTableCell>
        {cells}
      </SlimTableRow>
      {expandedDetail}
    </>
  );
}
